<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	//Stores
	import { user_settings, user_data, home_data } from 'src/stores/account/user';
	//Helpers
	import { getTimeFormat } from 'src/helpers/date_helper';
	import { weather_store } from 'src/helpers/geolocalion_helper';
	import { save_home } from 'src/helpers/home_helpers';
	//Design system components
	import { WeatherWidget, Label, Button } from '@Shelly/shelly-design-system';
	//App components
	import WeatherLocationModal from './WeatherLocationModal.svelte';
	import { celsius_to_fahrenheit } from 'src/helpers/interface_helpers';
	import { formatTitleToSnakeCase } from 'src/helpers/utilities';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	let showWeatherLocationModal = false;

	$: temperature =
		$home_data.weather_temp_unit === 'F'
			? `${celsius_to_fahrenheit($weather_store?.temperature.split('°')[0]) || 0}°`
			: $weather_store?.temperature;

	$: temperaturePrefix = $home_data.weather_temp_unit === 'F' ? 'F' : 'C';

	$: timeDetails = $weather_store?.forecast
		.map((forecast) => {
			const time = forecast.time.toString().split(' ')[4];
			const temp =
				$home_data.weather_temp_unit === 'F'
					? `${celsius_to_fahrenheit(forecast.temperature.split('°')[0])}F`
					: `${forecast.temperature.split('°')[0]}°C`;

			forecast = {
				title: getTimeFormat(time.split(':')[0], time.split(':')[1], false),
				icon: forecast.icon,
				text: temp,
				precipitation: forecast.pop,
			};

			return forecast;
		})
		.slice(0, 6); // slice weather time details to keep old weather component behaviour after forecast updated form 6 to 24 hours

	const goToPremiumSettings = () => {
		push('/settings/premium');
		setSidePanelState({ open: false });
	};

	const updateHomeData = () => {
		const dateNow = new Date();

		save_home('premiumWeatherWidgetAdvertise', {
			seen: true,
			date: dateNow,
		});

		home_data.set({
			...$home_data,
			premiumWeatherWidgetAdvertise: {
				seen: true,
				date: dateNow,
			},
		});
	};

	const windDirectionIconsMap = {
		N: 'fa-light fa-arrow-up',
		NNE: 'fa-light fa-arrow-up-right',
		NE: 'fa-light fa-arrow-up-right',
		ENE: 'fa-light fa-arrow-up-right',
		E: 'fa-light fa-arrow-right',
		ESE: 'fa-light fa-arrow-down-right',
		SE: 'fa-light fa-arrow-down-right',
		SSE: 'fa-light fa-arrow-down-right',
		S: 'fa-light fa-arrow-down',
		SSW: 'fa-light fa-arrow-down-left',
		SW: 'fa-light fa-arrow-down-left',
		WSW: 'fa-light fa-arrow-down-left',
		W: 'fa-light fa-arrow-left',
		WNW: 'fa-light fa-arrow-up-left',
		NW: 'fa-light fa-arrow-up-left',
		NNW: 'fa-light fa-arrow-up-left',
	};
</script>

{#if !$user_data?.premium && !$home_data?.premiumWeatherWidgetAdvertise?.seen}
	<WeatherWidget
		class="mb-s"
		image="./images/weather/Cloudy.png"
		rightActionIcon="fas fa-times"
		on:right-action-click={() => {
			updateHomeData();
			console.log('Dismisses WeatherWidget click');
		}}
	>
		<Label
			slot="leftBadge"
			size="s"
			icon="fas fa-gem"
			type="premium"
			value={$format('label_premium', { default: 'Premium' })}
			noRadiusType="second"
		/>
		<div class="d-flex justify-content-center width-full" slot="content">
			<Button
				slot="content"
				variant="premium"
				class="text-capitalize"
				size="m"
				on:click={goToPremiumSettings}
			>
				{$format('button_get_premium_weather', {
					default: 'get premium for weather widget',
				})}
			</Button>
		</div>
	</WeatherWidget>
{:else if $user_data.premium && $user_settings?.weather_station?.data?.stationName}
	<!-- if weather station is set & you have weather store info -->
	{#if $weather_store}
		<WeatherWidget
			class="mb-s"
			icon={$weather_store.icon}
			image={`./images/weather/${$weather_store.image}`}
			{temperature}
			{temperaturePrefix}
			type={$format(`weather_widget_type_${formatTitleToSnakeCase($weather_store.type)}`, {
				default: $weather_store.type,
			})}
			details={[
				{
					title: $format('weather_widget_precipitation', {
						default: 'Precipitation',
					}),
					text: $weather_store.pop,
				},
				{
					title: $format('weather_widget_humidity', { default: 'Humidity' }),
					text: $weather_store.humidity,
				},
				{
					title: $format('weather_widget_wind', { default: 'Wind' }),
					text: `${$weather_store.wind.split(' ')[0]} ${$weather_store.wind.split(' ')[1]}`,
					icon:
						Number($weather_store?.wind.split(' ')[0]) > 0
							? windDirectionIconsMap[$weather_store?.wind.split(' ')[2]]
							: '',
				},
			]}
			{timeDetails}
			action={{
				icon: 'fas fa-pen',
				title: `${$user_settings?.weather_station?.data?.stationName} / °${temperaturePrefix}`,
			}}
			on:edit-click={() => {
				if (!isSubAccount()) {
					showWeatherLocationModal = true;
				}
			}}
		/>
	{/if}
{:else if $user_data.premium}
	<!-- if weather station is is not set -->
	<WeatherWidget class="mb-s" image="./images/weather/Cloudy.png">
		<Label
			slot="leftBadge"
			size="s"
			icon="fas fa-gem"
			type="premium"
			value={$format('premium', { default: 'Premium' })}
			noRadiusType="second"
		/>
		<div class="d-flex justify-content-center width-full" slot="content">
			<Button
				slot="content"
				variant="premium"
				class="text-capitalize"
				size="m"
				on:click={() => (showWeatherLocationModal = true)}
			>
				{$format('button_add_weather_widget', {
					default: 'Add Weather Widget',
				})}
			</Button>
		</div>
	</WeatherWidget>
{/if}

{#if showWeatherLocationModal}
	<WeatherLocationModal bind:showModal={showWeatherLocationModal} />
{/if}
